import { Modal } from '@nextui-org/react';
import styled, { css } from 'styled-components';

import ZIndexLayers from '../../services/ZIndexLayers';
import { ThemeProp } from '../../theme/Themes';
import { determineBreakpoint } from '../../theme/utils';
import BottomSheet from '../BottomSheet';
import { ButtonRole } from '../Button';
import { BottomSheetChangeBreakpoint } from './Modal';
import { HeaderContainer } from './ModalHeader/ModalHeader.styles';

interface ModalProps {
  $color?: string;
  $bottomSheetChangeBreakpoint?: BottomSheetChangeBreakpoint;
  $maxHeight?: string;
  $minHeight?: string;
  $height?: string;
}

export const StyledModal = styled(Modal)<ModalProps>`
  display: flex;
  background: ${(props: ThemeProp & ModalProps) => props.$color ?? props.theme.colors.greyscale.c900};
  @media (max-width: ${(props: ThemeProp) => determineBreakpoint(props)}) {
    display: none;
  }
  &.nextui-modal {
    ${(props) => (props.fullScreen ? 'border-radius: 0' : 'border-radius: 0.375rem')};
    z-index: ${ZIndexLayers.modal} !important;
    ${(props) => props.$height && `height: ${props.$height};`}
    ${(props) => props.$maxHeight && `max-height: ${props.$maxHeight};`}
    ${(props) => props.$minHeight && `min-height: ${props.$minHeight};`}
  & .nextui-backdrop {
    z-index: ${ZIndexLayers.modal} !important;
  }

  ${HeaderContainer} {
    padding-bottom: 0 !important;
  }
`;

interface BottomSheetProps {
  $zIndex?: number;
  $noPadding?: boolean;
  $hideBackDrop?: boolean;
}

export const StyledBottomSheet = styled(BottomSheet).attrs((props: any & ModalProps & BottomSheetProps) => ({
  displayOnTablet: props.$bottomSheetChangeBreakpoint === BottomSheetChangeBreakpoint.Tablet,
  ...props,
}))`
  display: none;
  @media (max-width: ${(props: ThemeProp) => determineBreakpoint(props)}) {
    display: flex;
  }

  [data-rsbs-backdrop] {
    z-index: ${(props: BottomSheetProps) => (props.$zIndex ? props.$zIndex - 1 : ZIndexLayers.backdrop)};
  }
  [data-rsbs-overlay] {
    z-index: ${(props: BottomSheetProps) => props.$zIndex ?? ZIndexLayers.modal};
    ${(props) => props.$fullScreen && 'height: 100%;  max-height: 100%;'}
    background: ${(props: ThemeProp) => props.theme.colors.layout.surface.default};
  }

  [data-rsbs-scroll] {
    ${(props) => props.$fullScreen && 'display: flex; flex-direction: column; flex: 1;'}
    height: 100%;
    overflow: hidden;
  }

  [data-rsbs-content] {
    padding: 0;
    background: ${(props: ThemeProp & ModalProps) => props.$color ?? props.theme.colors.greyscale.c900};
    ${(props) => props.$fullScreen && 'flex: 1;'}
  }
  [data-rsbs-header] {
    background: ${(props: ThemeProp & ModalProps) => props.$color ?? props.theme.colors.greyscale.c900};
    ${(props) =>
      !props.$fullScreen && 'border-top-left-radius: 0.5rem; border-top-right-radius: 0.5rem; padding-bottom: 0.25rem;'}
  }
  ${(props) =>
    props.$noPadding &&
    ` && #modal_content {
    padding: 0;
  }`}
`;

/**
 * Hidden because there's a bug related to the buttons propagating the click event to buttons behind the dialog.
 */

// interface ButtonProps {
//   $buttonRole: ButtonRole;
// }

// export const StyledButton = styled(Button)<ButtonProps>`
//   color: ${(props) => props.theme.colors.greyscale.c800};
//   @media (max-width: ${(props: ThemeProp) => props.theme.breakpoints.mobile}) {
//     flex: 1;
//   }
// `;

// export const StyledSecondaryButton = styled(SecondaryButton)`
//   @media (max-width: ${(props: ThemeProp) => props.theme.breakpoints.mobile}) {
//     flex: 1;
//   }
// `;

interface ButtonProps {
  $bottomSheetChangeBreakpoint?: BottomSheetChangeBreakpoint;
  $buttonRole: ButtonRole;
}

export const ButtonDiv = styled.div<ButtonProps & any>`
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  cursor: ${(props) => (props.loading || props.disabled ? 'auto' : 'pointer')};
  border-radius: 3.375rem;
  font-size: ${(props: ThemeProp) => props.theme.fontSize.s};
  height: 2.5rem;

  padding: 0.688rem 1.141rem;
  color: ${(props) => (props.loading ? 'transparent' : props.color || props.theme.colors.greyscale.c900)};
  background: ${(props: ButtonProps & ThemeProp) =>
    props.$buttonRole === ButtonRole.Destructive
      ? `${props.theme.colors.content.error} !important`
      : props.theme.gradients.button.default};

  width: ${(props) => props.$width || 'unset'};

  &:focus {
    background: ${(props: ButtonProps & ThemeProp) =>
      props.$buttonRole === ButtonRole.Destructive
        ? `${props.theme.colors.content.error} !important`
        : props.theme.gradients.button.pressed};
  }

  &:hover {
    background: ${(props: ButtonProps & ThemeProp) =>
      props.$buttonRole === ButtonRole.Destructive
        ? `${props.theme.colors.content.error} !important`
        : props.theme.gradients.button.hover};
  }

  ${(props) =>
    !props.disabled &&
    `&:hover {
    background: ${props.theme.gradients.button.hover};
  };`}

  ${(props) =>
    props.disabled &&
    css`
      background: ${props.theme.colors.neutrals.c200};
      color: ${props.theme.colors.content.disabled};

      &:hover {
        background: ${props.theme.colors.neutrals.c200};
      }
    `}

  @media (max-width: ${(props: ThemeProp) => determineBreakpoint(props)}) {
    flex: 1;
    height: 3rem;
    font-size: ${({ theme }: ThemeProp) => theme.fontVariants.desktop.body1.fontSize};
    box-shadow: none;
  }
`;

export const SecondaryButtonDiv = styled(ButtonDiv)<{ $bottomSheetChangeBreakpoint?: BottomSheetChangeBreakpoint }>`
  background: ${(props: ThemeProp) => props.theme.colors.greyscale.c900};
  border: 1px solid ${({ theme }: ThemeProp) => theme.colors.neutrals.c200};
  color: ${({ theme }: ThemeProp) => theme.colors.neutrals.c800};

  &:hover {
    background: ${(props: ThemeProp) => props.theme.colors.neutrals.c100};
    color: ${(props: ThemeProp) => props.theme.colors.content.default};
  }
  &:active {
    background: ${(props: ThemeProp) => props.theme.colors.main.c100};
    color: ${(props: ThemeProp) => props.theme.colors.content.default};
  }
`;

interface FooterProps {
  reverse?: boolean;
  $showMobileBorderTop?: boolean;
  $bottomSheetChangeBreakpoint?: BottomSheetChangeBreakpoint;
}

export const StyledFooter = styled.div<FooterProps>`
  display: flex;
  flex-direction: ${(props: FooterProps) => (props.reverse ? 'row-reverse' : 'row')};
  width: 100%;
  gap: 1rem;
  justify-content: ${(props: FooterProps) => (props.reverse ? 'flex-start' : 'flex-end')};
  padding: 0 1.5rem 1.5rem 1.5rem;

  @media (max-width: ${(props: ThemeProp) => determineBreakpoint(props)}) {
    padding: 1.5rem;
    ${(props: FooterProps & ThemeProp) =>
      props.$showMobileBorderTop &&
      css`
        border-top: 1px solid ${props.theme.colors.neutrals.c100};
      `};
  }
`;

export const ContentContainer = styled.div<{ $padding?: string; $disableScroll?: boolean }>`
  padding: ${(props) => props.$padding || '0 1.5rem'};
  width: 100%;
  height: 100%;
  position: relative;
  overflow: ${(props) => (props.$disableScroll ? 'hidden' : 'scroll')};
  // Max height allowed in order to enable scrolling when needed
  ${(props) => !props.$disableScroll && 'max-height: 71dvh;'}
`;
